import { Fragment, type ReactNode } from 'react';
import { useElementWidth } from './useElementWidth';
import { Link } from 'react-admin';
import { Box, Typography } from '@mui/material';
import { alpha, type Theme } from '@mui/material/styles';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { shortDateTime } from '../../shared/dateTime';
import { TipLayer } from './GanttTips';
import { useFeature } from '../featureFlags/FeatureGate';
import { GANTT_WINDOW_BRACKETS, OCCUPIED_UNTIL_TMS_PLAN, RUNNING_ORDER_NOT_VARIANT } from './fleetFeatures';
import { WindowBrackets } from './WindowBrackets';
import type { WindowBracket } from './bracketRules';
import { phaseTones, segmentFill, tmsPlanFill } from './PlanTimeline';
import {
  notVariantLabel,
  planPhase,
  planRowStatus,
  rangeText,
  runningInTms,
  tmsStatusText,
  type PlanPhase,
} from './planRows';
import {
  durationText,
  hasSegments,
  occupiedUntilLine,
  segmentNotesLines,
  segmentRange,
  segmentTitle,
  tmsPlanTitle,
} from './planSegments';
import { driverWeekBounds, driverWeekText, driverWeekTitle, isReplicatedWeek } from './driverWeeks';
import { unavailableMonthTitle } from './carScheduleApi';
import { barGeometry, nowOffset, packLanes, routeText, timelineDays, type TimelineWindow } from './timeline';
import {
  activityDrawable,
  dayHeaderLayout,
  estimateStart,
  fitBarLabel,
  estimatedActivity,
  ganttLanes,
  hourLabels,
  hourParts,
  hoursText,
  itemExtent,
  labelWidthPx,
  laneOf,
  lateSpan,
  orderWindows,
  staleCaption,
  weeklyDriving,
  type GanttDetail,
  type GanttLane,
  type WeekDriving,
} from './ganttRules';
import type {
  DriverActivity,
  DriverActivityKind,
  DriverWeek,
  PlanItem,
  PlanMonth,
  PlanSegment,
} from './vehiclePlanApi';
import type { TmsAlert } from '../userOffers/orderActionsApi';
import { LATENESS_HEADING, latenessLines, latenessOf } from '../userOffers/variantWindow';

/**
 * Oś harmonogramu na karcie pojazdu (07.10.2026, prototyp p. Mariusza
 * `pojazd-BQO1011-v2.html`; odstępstwa — `UWAGI.md` przy prototypie).
 *
 * ## Wiersze
 *
 * „W trakcie / Zaplanowane / Zakończone” (zmiana 12) po stanie z TMS
 * (`planRows.planPhase`, wariant w zaplanowanych). Zlecenie to trzy
 * elementy: okno załadunku (Z), realizacja (pasek szacunku w odcinkach
 * z `plan-segments.ts`, koniec = `occupancyEnd`), okno rozładunku (R).
 * Okna Z/R rysujemy z terminów punktów z planu TMS tylko wtedy, gdy różnią
 * się od okna klienta — inaczej jedno okno klienta z podpisem (UWAGI.md).
 * Zakończone — bez okien (zmiana 14), wiersz zwarty. Pusty wiersz zwija się
 * do cienkiej linii z podpisem (zmiana 13). Nachodzące pozycje idą na osobne
 * podtory, żeby konflikt było widać.
 *
 * Widok uproszczony nakłada okna na siebie i stawia na nich pasek; pełny
 * rozkłada je na trzy tory (zmiana 6).
 *
 * ## Spóźnienie i alert
 *
 * Gdy szacunek wykonania wychodzi poza okno rozładunku, za końcem okna stoi
 * kreskowany pomarańczowy odcinek do szacowanego końca (`overrunUntil`).
 * Gdy plan TMS przy punktach kończy zlecenie później niż szacunek, za
 * paskiem stoi stonowany odcinek „wg planu TMS” (`tmsPlanSegment`,
 * 12.10.2026) — sama informacja, reguły liczą szacunkiem.
 * Obsadzenie z alertem czekającym na potwierdzenie (przeplanowanie, zmiana
 * w TMS) ma ikonę ostrzeżenia na pasku i pomarańczową obwódkę; treść alertu
 * jest w dymku.
 *
 * ## Aktywność kierowcy
 *
 * Wiersz pod zleceniami: kierowca tygodnia z harmonogramu kierowców (górny
 * tor), czynności z osi czasu tachografu (eco-truck) i dalej — od końca osi
 * — plan jazdy z odcinków obsadzeń jako szacunek (środkowy tor), tygodniowy
 * czas jazdy wobec limitu 56 h (dolny tor; przekroczenie na czerwono
 * z procentem). Bez danych z tachografu — zdanie z powodem, bez rysowania
 * (UWAGI.md: zamiast „symulacji” z prototypu).
 *
 * ## Mieści się w karcie
 *
 * Bez przewijania w bok przy 1366 i 1440 px: oś ma szerokość karty
 * (`minmax(0, 1fr)`), dni dzielą ją po równo w czasie (doba zmiany czasu jest
 * odpowiednio węższa albo szersza — jak pasek).
 */

const LABEL_WIDTH = 118;
const TASK_H = 26;
const ROW_FULL = 98;
const ROW_SIMPLE = 49;
const ROW_EMPTY = 26;
const DRIVER_ROW = 98;
/** Najwęższy pasek, przy którym mieści się długość obok numeru. */
const HOURS_MIN_PX = 96;
/** Czcionka podpisu paska — 0,7 rem (`OrderBar`). */
const BAR_FONT_PX = 11.2;

/**
 * Miejsce podpisu obok paska, który go nie mieści (29.10.2026): za prawym
 * końcem, a gdy tam brak miejsca do końca osi — przed lewym. `null` bez
 * szerokości osi.
 */
function outsideLabelBox(
  g: { left: number; width: number },
  text: string,
  trackWidth: number,
  fontPx: number,
): { leftPct: number; text: string } | null {
  if (!(trackWidth > 0)) return null;
  const w = labelWidthPx(text, fontPx) + 4;
  const rightPx = ((g.left + g.width) * trackWidth) / 100;
  if (rightPx + w <= trackWidth) return { leftPct: ((rightPx + 2) / trackWidth) * 100, text };
  const leftPx = (g.left * trackWidth) / 100 - w - 2;
  return { leftPct: (Math.max(leftPx, 0) / trackWidth) * 100, text };
}

/** Kolory czynności kierowcy — jak w prototypie. */
export const ACTIVITY_COLORS: Record<DriverActivityKind, { bg: string; fg: string }> = {
  drive: { bg: '#1e88e5', fg: '#fff' },
  work: { bg: '#8e24aa', fg: '#fff' },
  break: { bg: '#fbc02d', fg: 'rgba(0,0,0,0.87)' },
  rest: { bg: '#b2dfdb', fg: 'rgba(0,0,0,0.75)' },
  availability: { bg: '#cfd8dc', fg: 'rgba(0,0,0,0.75)' },
  unknown: { bg: '#eceff1', fg: 'rgba(0,0,0,0.6)' },
};

/** Neutralny kolor wiersza kierowcy i tygodniowego czasu jazdy (prototyp: „slate”). */
const SLATE = '#546e7a';

export const ACTIVITY_LABELS: Record<DriverActivityKind, string> = {
  drive: 'Jazda',
  work: 'Praca',
  break: 'Przerwa',
  rest: 'Odpoczynek',
  availability: 'Dyspozycyjność',
  unknown: 'Czynność nieznana',
};

/** Ostrzeżenie — jedyny pomarańcz na osi (zmiana 4). */
const WARNING_ORANGE = '#ed6c02';

const ALERT_TITLES: Record<TmsAlert['kind'], string> = {
  replan: 'Rekomendowane przeplanowanie',
  change: 'Zmiana w TMS po obsadzeniu',
  cancellation: 'Możliwa anulacja zlecenia w TMS',
  vehicleChange: 'Zmiana pojazdu w TMS po obsadzeniu',
};

/** Linie alertów do dymku paska i komórki tabeli. */
export function alertTipLines(alerts: readonly TmsAlert[] | undefined): string[] {
  return (alerts ?? []).map(
    (a) => `⚠ ${ALERT_TITLES[a.kind]} (wykryto ${shortDateTime(a.at)}): ${a.text}${a.reason ? `\n${a.reason}` : ''}`,
  );
}

/**
 * Dymek całego zlecenia — pasek, odcinki i komórka numeru w tabeli.
 * `occupiedUntil` — linia „Zajęty do” tylko z przełącznikiem
 * `occupiedUntilTmsPlan` (12.10.2026).
 */
export function orderTip(item: PlanItem, occupiedUntil = true, runningNotVariant = false): string {
  const w = orderWindows(item);
  const lines = [
    item.number ?? `#${item.orderId}`,
    routeText(item),
    `${planPhase(item) === 'planned' ? 'Zaplanowana realizacja' : 'Realizacja'} (${
      item.occupancyBasis === 'estimate' ? 'szacunek zajętości' : 'całe okno zlecenia — trasa nieznana'
    }): ${rangeText(item.occupancyFrom, item.occupancyTo)}`,
    `Okno zlecenia od klienta: ${rangeText(item.windowFrom, item.windowTo)}`,
  ];
  // „Zajęty do” ze źródłem (12.10.2026) — plan TMS, gdy kończy później.
  const until = occupiedUntil ? occupiedUntilLine(item) : null;
  if (until) lines.push(until);
  if (w.mode === 'points') {
    if (w.loading) lines.push(`Okno załadunku (plan TMS): ${rangeText(w.loading.from, w.loading.to)}`);
    if (w.unloading) lines.push(`Okno rozładunku (plan TMS): ${rangeText(w.unloading.from, w.unloading.to)}`);
  }
  lines.push(`Stan: ${planRowStatus(item).label}`);
  const tms = tmsStatusText(item);
  if (tms) lines.push(tms);
  if (runningNotVariant && runningInTms(item)) {
    // S-24.10-06: kandydatura do zlecenia, które już jedzie — nie wariant; nic nie zwalniamy samo.
    lines.push(`Kandydatura pojazdu — ${notVariantLabel(item)}. Nie zwalniamy jej automatycznie.`);
  } else if (item.kind === 'candidate') {
    lines.push('Wariant — wstępne wskazanie pojazdu, nie obsadzenie.');
  }
  if (item.assignedElsewhere) lines.push('Zlecenie jest już obsadzone innym pojazdem.');
  const late = lateSpan(item);
  if (late) {
    const over = new Date(late.to).getTime() - new Date(late.from).getTime();
    lines.push(
      `Szacowany koniec wykonania: ${shortDateTime(late.to)} — ${durationText(over)} po końcu okna rozładunku (szacunek).`,
    );
    // Obie liczby spóźnienia z opisem źródła (22.10.2026, decyzja użytkownika pkt 4);
    // od 23.10.2026 obie mierzą koniec rozładunku — mówi to nagłówek.
    const both = latenessOf(item.lateness);
    if (both) lines.push(`${LATENESS_HEADING}:`, ...latenessLines(both).map((l) => `– ${l}`));
  }
  lines.push(...segmentNotesLines(item));
  const alerts = alertTipLines(item.alerts);
  if (alerts.length > 0) lines.push('', ...alerts);
  return lines.join('\n');
}

/** Podgląd zlecenia z rekomendacji na osi (zmiana 15). */
export interface GanttPreview {
  label: string;
  from: string;
  /** Szacowany koniec (rozładunek); brak — sam znacznik załadunku. */
  to: string | null;
  /** Nie zdąży / po oknie — czerwony zamiast zielonego. */
  late: boolean;
  /**
   * Odcinki szacunku — zlecenie tak, jak stałoby na osi jako wariant tego
   * auta (15.10.2026, makieta FTL Etap 1, `vehiclePreviewSegments`; API
   * `GET /car-schedules/preview`). Brak — podgląd jednym paskiem `from`–`to`.
   */
  segments?: PlanSegment[];
  /** Szacowany koniec po końcu okna rozładunku — odcinek spóźnienia podglądu. */
  overrunUntil?: string | null;
  /** Koniec okna zlecenia — początek odcinka spóźnienia. */
  windowTo?: string | null;
  /**
   * Nawiasy Z/R podglądu (24.10.2026, S-24.10-05) — zlecenie bez obsadzenia
   * też ma okna klienta na osi (decyzja z 21.10: „także harmonogram zlecenia
   * bez obsadzenia”). Brak — podgląd bez nawiasów.
   */
  windowBrackets?: WindowBracket[];
  /** Zlecenie podglądu — do chmurki nawiasu. */
  orderId?: number;
}

interface Props {
  window: TimelineWindow;
  days: number;
  detail: GanttDetail;
  items: PlanItem[];
  months: PlanMonth[];
  driverWeeks: DriverWeek[];
  /** `null` — wczytuje się albo błąd (`activityError`). */
  activity: DriverActivity | null;
  activityError?: string | null;
  now?: Date;
  /** Zlecenie podświetlone (zmiana 11) — wspólne z tabelą pod osią. */
  highlight: number | null;
  onHighlight: (orderId: number | null) => void;
  preview?: GanttPreview | null;
  /**
   * Osobny wiersz „Warianty” (15.10.2026, makieta FTL Etap 1,
   * `vehicleVariantsLane`); bez niego warianty stoją w „Zaplanowane”.
   */
  variantsLane?: boolean;
  /**
   * Pasek osi nad rekomendowanymi zleceniami (15.10.2026, przegląd Etapu 1
   * przy 1366×768): same wiersze „Zaplanowane” (i „Warianty”), bez
   * „Aktywności kierowcy”. Wiersz „Zaplanowane” ma stałą wysokość — podgląd
   * nie zmienia wysokości paska, więc tabela pod nim nie skacze pod myszą.
   */
  compact?: boolean;
  /**
   * Okna klienta jako nawiasy Z/R (24.10.2026, S-24.10-05) — ustawia sam
   * wykres z przełącznika `ganttWindowBrackets`; pozycja bez nawiasów z API
   * zostaje przy oknach jak dotąd.
   */
  windowBrackets?: boolean;
  /**
   * Zlecenie w realizacji w TMS nie jest wariantem (24.10.2026, S-24.10-06) —
   * ustawia sam wykres z przełącznika `runningOrderNotVariant`.
   */
  runningNotVariant?: boolean;
  /**
   * Przesunięcie osi na dzień (S-24.10-07: „Pokaż ostatni odczyt”, gdy oś
   * tachografu jest starsza niż parametr i jej koniec jest poza kadrem).
   */
  onShowDate?: (at: Date) => void;
}

type Geometry = NonNullable<ReturnType<typeof barGeometry>>;

/** Wiersze paska nad rekomendacjami — tam, gdzie staje podgląd, i warianty obok. */
const COMPACT_LANES: readonly GanttLane[] = ['planned', 'variants'];

const clipRadius = (g: Geometry) => ({
  borderTopLeftRadius: g.clippedStart ? 0 : undefined,
  borderBottomLeftRadius: g.clippedStart ? 0 : undefined,
  borderTopRightRadius: g.clippedEnd ? 0 : undefined,
  borderBottomRightRadius: g.clippedEnd ? 0 : undefined,
});

const placed = (g: Geometry, top: number, height: number = TASK_H) => ({
  position: 'absolute' as const,
  left: `${g.left}%`,
  width: `${g.width}%`,
  top,
  height,
  boxSizing: 'border-box' as const,
});

/** Układ wiersza zleceń: pozycje w oknie, podtory, wysokość. */
interface LaneLayout {
  items: PlanItem[];
  lanes: number[];
  subHeight: number;
  barTop: number;
  empty: boolean;
  height: number;
}

function laneLayout(lane: GanttLane, all: PlanItem[], props: Props): LaneLayout {
  const withWindows = lane !== 'done';
  const subHeight = withWindows && props.detail === 'full' ? ROW_FULL : ROW_SIMPLE;
  const from = props.window.from.getTime();
  const to = props.window.to.getTime();
  // Tylko to, co widać w oknie — pozycje spoza kadru nie zajmują podtorów.
  const brackets = withWindows && props.windowBrackets === true;
  const items = all.filter((i) => {
    const e = itemExtent(i, withWindows, brackets);
    return e !== null && e.end >= from && e.start <= to;
  });
  const { lanes, count } = packLanes(
    items.map((i) => itemExtent(i, withWindows, brackets) as { start: number; end: number }),
  );
  const empty = items.length === 0 && !(lane === 'planned' && props.preview);
  // Pasek nad rekomendacjami: miejsce na podgląd zarezerwowane zawsze.
  const reserved = props.compact === true && lane === 'planned';
  return {
    items,
    lanes,
    subHeight,
    barTop: withWindows && props.detail === 'full' ? 36 : 11,
    empty,
    height: reserved ? Math.max(count, 1) * subHeight : empty ? ROW_EMPTY : count * subHeight,
  };
}

/** Pusty wiersz — podpis (zmiana 13). */
const EMPTY_TEXT: Record<GanttLane, string> = {
  inProgress: 'brak zleceń w tym okresie',
  planned: 'brak zleceń w tym okresie',
  done: 'brak zleceń w tym okresie',
  variants: 'brak wariantów w tym okresie',
};

/** Wiersz zleceń osi — jeden z trzech (z makietą FTL: czterech). */
const LaneTrack = ({
  lane,
  layout,
  props,
  trackWidth,
  background,
}: {
  lane: GanttLane;
  layout: LaneLayout;
  props: Props;
  trackWidth: number;
  background: ReactNode;
}) => {
  const { window, detail, highlight, preview } = props;
  const withWindows = lane !== 'done';
  return (
    <Box
      data-testid={`gantt-track-${lane}`}
      data-empty={layout.empty ? 'true' : undefined}
      sx={{
        position: 'relative',
        height: layout.height,
        borderBottom: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        transition: 'height .2s',
      }}
    >
      {background}
      {layout.empty ? (
        <Typography
          variant="caption"
          sx={{ position: 'absolute', left: 8, top: 4, color: 'text.secondary', letterSpacing: 0.4 }}
        >
          {EMPTY_TEXT[lane]}
        </Typography>
      ) : null}
      {layout.items.map((item, idx) => {
        const y = layout.lanes[idx] * layout.subHeight;
        const hl = highlight === item.orderId;
        return (
          <Fragment key={`${item.kind}-${item.orderId}`}>
            {/* Kolor pozycji — stan z TMS (`planPhase`), także w wierszu
                „Warianty”; bez niego wiersz = stan, więc wynik ten sam. */}
            {withWindows && props.windowBrackets && item.windowBrackets ? (
              // Nawiasy Z/R (24.10.2026, S-24.10-05) zamiast okien — w wierszu paska.
              <WindowBrackets
                item={item}
                window={window}
                barTop={y + layout.barTop}
                barHeight={TASK_H}
                hook={detail === 'full' ? 20 : 7}
                tipAttr="data-tip"
                labels
              />
            ) : withWindows ? (
              <OrderWindows item={item} lane={planPhase(item)} y={y} detail={detail} highlighted={hl} window={window} />
            ) : null}
            <OrderBar
              item={item}
              lane={planPhase(item)}
              top={y + layout.barTop}
              window={window}
              trackWidth={trackWidth}
              highlighted={hl}
            />
          </Fragment>
        );
      })}
      {lane === 'planned' && preview ? <PreviewBar preview={preview} window={window} top={layout.barTop} /> : null}
      {lane === 'planned' && preview && props.windowBrackets && preview.windowBrackets && preview.segments ? (
        <WindowBrackets
          item={{
            orderId: preview.orderId ?? 0,
            number: preview.label,
            segments: preview.segments,
            overrunUntil: preview.overrunUntil ?? undefined,
            windowBrackets: preview.windowBrackets,
          }}
          window={window}
          barTop={layout.barTop}
          barHeight={TASK_H}
          hook={detail === 'full' ? 20 : 7}
          tipAttr="data-tip"
          labels
        />
      ) : null}
    </Box>
  );
};

/** Okna Z i R (albo jedno okno klienta) jednej pozycji. */
const OrderWindows = ({
  item,
  lane,
  y,
  detail,
  highlighted,
  window,
}: {
  item: PlanItem;
  lane: PlanPhase;
  y: number;
  detail: GanttDetail;
  highlighted: boolean;
  window: TimelineWindow;
}) => {
  const w = orderWindows(item);
  const head = `${item.number ?? `#${item.orderId}`}\n${routeText(item)}`;
  const simple = detail === 'simple';
  const boxes: Array<{
    key: string;
    span: { from: string; to: string } | null;
    label: string;
    tip: string;
    top: number;
    height: number;
    client: boolean;
    align: 'left' | 'right';
  }> =
    w.mode === 'client'
      ? [
          {
            key: 'client',
            span: w.client,
            label: 'okno klienta',
            tip: `Okno zlecenia od klienta (załadunek i rozładunek): ${rangeText(w.client?.from, w.client?.to)}\n${head}`,
            top: simple ? y + 5 : y + 3,
            height: simple ? 38 : ROW_FULL - 6,
            client: true,
            align: 'left',
          },
        ]
      : [
          {
            key: 'loading',
            span: w.loading,
            label: 'Z',
            tip: `Okno załadunku (plan TMS): ${rangeText(w.loading?.from, w.loading?.to)}\nOkno klienta: ${rangeText(w.client?.from, w.client?.to)}\n${head}`,
            top: y + 5,
            height: simple ? 38 : TASK_H,
            client: false,
            align: 'left',
          },
          {
            key: 'unloading',
            span: w.unloading,
            label: 'R',
            tip: `Okno rozładunku (plan TMS): ${rangeText(w.unloading?.from, w.unloading?.to)}\nOkno klienta: ${rangeText(w.client?.from, w.client?.to)}\n${head}`,
            top: simple ? y + 5 : y + 67,
            height: simple ? 38 : TASK_H,
            client: false,
            align: 'right',
          },
        ];
  return (
    <>
      {boxes.map((b) => {
        const g = b.span ? barGeometry(b.span.from, b.span.to, window) : null;
        if (!g) return null;
        return (
          <Box
            key={b.key}
            data-testid={`gantt-window-${b.key}-${item.orderId}`}
            data-order={item.orderId}
            data-tip={b.tip}
            sx={[
              placed(g, b.top, b.height),
              {
                borderRadius: 1,
                ...clipRadius(g),
                px: 0.5,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                fontSize: '0.7rem',
                fontWeight: 600,
                lineHeight: `${TASK_H - 2}px`,
                textAlign: b.align,
                zIndex: 0,
              },
              (theme: Theme) => {
                const c = phaseTones(theme, lane).main;
                return b.client
                  ? {
                      border: `1px dashed ${theme.palette.text.disabled}`,
                      backgroundColor: alpha(c, highlighted ? 0.16 : 0.06),
                      color: simple ? 'transparent' : theme.palette.text.secondary,
                    }
                  : {
                      border: `1px solid ${alpha(c, 0.55)}`,
                      backgroundColor: alpha(c, highlighted ? 0.4 : 0.22),
                      color: simple ? 'transparent' : theme.palette.text.primary,
                    };
              },
            ]}
          >
            {b.label}
          </Box>
        );
      })}
    </>
  );
};

/** Pasek realizacji: odcinki szacunku (albo jeden blok), spóźnienie, ikona alertu. */
const OrderBar = ({
  item,
  lane,
  top,
  window,
  trackWidth,
  highlighted,
}: {
  item: PlanItem;
  lane: PlanPhase;
  top: number;
  window: TimelineWindow;
  trackWidth: number;
  highlighted: boolean;
}) => {
  const g = barGeometry(item.occupancyFrom, item.occupancyTo, window);
  // „Zajęte do” wg planu TMS tylko z przełącznikiem (12.10.2026).
  const tmsPlanOn = useFeature(OCCUPIED_UNTIL_TMS_PLAN);
  const runningNotVariant = useFeature(RUNNING_ORDER_NOT_VARIANT);
  const tip = orderTip(item, tmsPlanOn, runningNotVariant);
  const variant = item.kind === 'candidate';
  const alerts = item.alerts ?? [];
  const late = lateSpan(item);
  const lateG = late ? barGeometry(late.from, late.to, window, 0) : null;
  const tmsPlan = tmsPlanOn ? (item.tmsPlanSegment ?? null) : null;
  const tmsG = tmsPlan ? barGeometry(tmsPlan.start, tmsPlan.end, window, 0) : null;
  const segmented = hasSegments(item);
  const px = g ? (g.width * trackWidth) / 100 : 0;
  const durationMs =
    item.occupancyFrom && item.occupancyTo
      ? new Date(item.occupancyTo).getTime() - new Date(item.occupancyFrom).getTime()
      : 0;
  const showHours = px >= HOURS_MIN_PX && g !== null && !g.clippedEnd && durationMs > 0;
  const notVariant = runningNotVariant && runningInTms(item);
  const orderNumber = item.number ?? `#${item.orderId}`;
  const clipMark = g?.clippedStart ? '‹ ' : '';
  // Numer dopasowany do szerokości paska (29.10.2026): koniec numeru zamiast
  // urwanego początku, a gdy nic się nie mieści — obok paska. Miejsce na tekst:
  // pasek bez odstępów (2 × 4 px), ikony alertu (16 + 4 px) i długości.
  const textPx =
    px - 8 - (alerts.length > 0 ? 20 : 0) - (showHours ? labelWidthPx(hoursText(durationMs / 3_600_000), BAR_FONT_PX) + 4 : 0);
  const fitted = fitBarLabel(
    `${clipMark}${orderNumber}`,
    notVariant ? ' · kandydatura' : variant ? ' · wariant' : '',
    trackWidth > 0 ? Math.max(textPx, 0.01) : 0,
    BAR_FONT_PX,
  );
  const name = fitted.outside ? '' : fitted.text;
  const outsideLabel = fitted.outside && g ? outsideLabelBox(g, fitted.text, trackWidth, BAR_FONT_PX) : null;
  const ariaLabel = `Zlecenie ${item.number ?? `#${item.orderId}`}, ${routeText(item)}, ${planRowStatus(item).label}${
    alerts.length > 0 ? ', alert' : ''
  }`;

  return (
    <>
      {segmented
        ? (item.segments ?? []).map((s, idx) => {
            const sg = barGeometry(s.start, s.end, window, 0);
            if (!sg || sg.width <= 0) return null;
            return (
              <Link
                key={`${s.kind}-${s.start}`}
                to={`/user-offers/${item.orderId}/show`}
                tabIndex={-1}
                data-testid={`gantt-segment-${item.kind}-${item.orderId}-${idx}`}
                data-kind={s.kind}
                data-order={item.orderId}
                data-tip={segmentTitle(s, tip)}
                sx={[
                  placed(sg, top),
                  { display: 'block', zIndex: 1, opacity: variant ? 0.5 : 1 },
                  (theme: Theme) => segmentFill(theme, lane, s.kind),
                ]}
              />
            );
          })
        : null}
      {tmsPlan && tmsG && tmsG.width > 0 ? (
        <Link
          to={`/user-offers/${item.orderId}/show`}
          tabIndex={-1}
          data-testid={`gantt-tms-${item.kind}-${item.orderId}`}
          data-kind="tmsPlan"
          data-order={item.orderId}
          data-tip={tmsPlanTitle(tmsPlan, tip)}
          sx={[
            placed(tmsG, top),
            { display: 'block', zIndex: 1, opacity: variant ? 0.6 : 1, borderRadius: '0 4px 4px 0', ...clipRadius(tmsG) },
            (theme: Theme) => tmsPlanFill(theme),
          ]}
        />
      ) : null}
      {lateG ? (
        <Box
          data-testid={`gantt-late-${item.orderId}`}
          sx={[
            placed(lateG, top),
            {
              zIndex: 3,
              pointerEvents: 'none',
              border: '1px solid #c75a00',
              borderLeft: 0,
              borderRadius: '0 4px 4px 0',
              ...clipRadius(lateG),
              backgroundImage: `repeating-linear-gradient(135deg, ${WARNING_ORANGE} 0 6px, #f7a65a 6px 12px)`,
            },
          ]}
        />
      ) : null}
      {g ? (
        <Link
          to={`/user-offers/${item.orderId}/show`}
          aria-label={ariaLabel}
          data-testid={`gantt-bar-${item.kind}-${item.orderId}`}
          data-order={item.orderId}
          data-tip={tip}
          data-alert={alerts.length > 0 ? 'true' : undefined}
          data-highlighted={highlighted ? 'true' : undefined}
          data-segmented={segmented ? 'true' : undefined}
          sx={[
            placed(g, top),
            {
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: alerts.length > 0 ? 26 : 14,
              px: 0.5,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              fontSize: '0.7rem',
              lineHeight: `${TASK_H - 2}px`,
              textDecoration: 'none',
              borderRadius: 1,
              ...clipRadius(g),
              zIndex: 2,
              // Nad odcinkami numer nie zasłania ich dymków — mysz trafia
              // w odcinek, klawiatura w pasek (jeden przystanek na zlecenie).
              pointerEvents: segmented ? 'none' : undefined,
              '&:focus-visible': { outline: '2px solid', outlineColor: 'text.primary', outlineOffset: '-2px' },
            },
            (theme: Theme) => {
              const c = phaseTones(theme, lane);
              const ring = alerts.length > 0 ? `0 0 0 2px ${WARNING_ORANGE}` : undefined;
              const hlRing = highlighted ? '0 0 0 2px rgba(0,0,0,0.55)' : undefined;
              const shadow = [ring, hlRing].filter(Boolean).join(', ') || undefined;
              if (variant) {
                return {
                  border: `1px dashed ${
                    item.assignedElsewhere ? theme.palette.error.main : theme.palette.success.dark
                  }`,
                  backgroundColor: segmented ? 'transparent' : theme.palette.background.paper,
                  color: theme.palette.text.primary,
                  boxShadow: shadow,
                };
              }
              return segmented
                ? {
                    border: `1px solid ${alpha('#000', 0.2)}`,
                    color: theme.palette.common.white,
                    textShadow: '0 0 3px rgba(0,0,0,0.8)',
                    boxShadow: shadow,
                  }
                : {
                    border: `1px solid ${alpha('#000', 0.2)}`,
                    backgroundColor: c.main,
                    color: theme.palette.common.white,
                    opacity: item.occupancyBasis === 'window' ? 0.75 : 1,
                    boxShadow: shadow,
                    filter: highlighted ? 'brightness(0.85)' : undefined,
                  };
            },
          ]}
        >
          {alerts.length > 0 ? (
            <WarningAmberRoundedIcon
              data-testid={`gantt-alert-${item.orderId}`}
              sx={{ fontSize: 16, color: WARNING_ORANGE, bgcolor: 'common.white', borderRadius: '50%', flexShrink: 0 }}
            />
          ) : null}
          <Box
            component="span"
            data-testid={`gantt-bar-label-${item.orderId}`}
            sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {name}
          </Box>
          {showHours ? (
            <Box component="span" sx={{ flex: 'none', fontWeight: 500 }}>
              {hoursText(durationMs / 3_600_000)}
            </Box>
          ) : null}
        </Link>
      ) : null}
      {outsideLabel ? (
        <Box
          aria-hidden
          data-testid={`gantt-bar-outside-label-${item.orderId}`}
          sx={{
            position: 'absolute',
            left: `${outsideLabel.leftPct}%`,
            top,
            height: TASK_H,
            lineHeight: `${TASK_H}px`,
            px: '2px',
            fontSize: '0.7rem',
            whiteSpace: 'nowrap',
            color: 'text.primary',
            pointerEvents: 'none',
            zIndex: 2,
          }}
        >
          {outsideLabel.text}
        </Box>
      ) : null}
    </>
  );
};

/**
 * Odcinki podglądu (15.10.2026, makieta FTL Etap 1): dolot, załadunek,
 * przejazd z przerwami i odpoczynkiem, rozładunek — kolory odcinków
 * zaplanowanych, przygaszone, z przerywaną ramką całości i spóźnieniem po
 * oknie. To szacunek tej samej reguły co pasek wariantu; nic nie zapisuje.
 */
const PreviewSegments = ({
  preview,
  segments,
  window,
  top,
}: {
  preview: GanttPreview;
  segments: PlanSegment[];
  window: TimelineWindow;
  top: number;
}) => {
  const color = preview.late ? 'error.main' : 'success.main';
  const first = segments[0];
  const last = segments[segments.length - 1];
  const whole = barGeometry(first.start, last.end, window, 0);
  const late =
    preview.overrunUntil && preview.windowTo ? barGeometry(preview.windowTo, preview.overrunUntil, window, 0) : null;
  if (!whole || whole.width <= 0) {
    // Poza kadrem — strzałka przy krawędzi, jak podgląd jednym paskiem.
    return <PreviewBar preview={{ ...preview, segments: undefined }} window={window} top={top} />;
  }
  return (
    <>
      {segments.map((s, i) => {
        const sg = barGeometry(s.start, s.end, window, 0);
        if (!sg || sg.width <= 0) return null;
        return (
          <Box
            key={`${s.kind}-${s.start}`}
            data-testid={`gantt-preview-segment-${i}`}
            data-kind={s.kind}
            sx={[
              placed(sg, top),
              { zIndex: 4, opacity: 0.75, pointerEvents: 'none' },
              (theme: Theme) => segmentFill(theme, 'planned', s.kind),
            ]}
          />
        );
      })}
      {late && late.width > 0 ? (
        <Box
          data-testid="gantt-preview-late"
          sx={[
            placed(late, top),
            {
              zIndex: 4,
              pointerEvents: 'none',
              borderRadius: '0 4px 4px 0',
              ...clipRadius(late),
              backgroundImage: `repeating-linear-gradient(135deg, ${WARNING_ORANGE} 0 6px, #f7a65a 6px 12px)`,
              opacity: 0.8,
            },
          ]}
        />
      ) : null}
      <Box
        data-testid="gantt-preview"
        data-segmented="true"
        sx={[
          placed(whole, top),
          {
            px: 0.5,
            lineHeight: `${TASK_H - 4}px`,
            fontSize: '0.7rem',
            fontWeight: 600,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            border: '2px dashed',
            borderColor: color,
            borderRadius: 1,
            color: 'text.primary',
            textShadow: '0 0 3px #fff',
            zIndex: 5,
            pointerEvents: 'none',
            ...clipRadius(whole),
          },
        ]}
      >
        {preview.label}
      </Box>
    </>
  );
};

/** Podgląd rekomendowanego zlecenia — przerywany pasek albo strzałka przy krawędzi. */
const PreviewBar = ({ preview, window, top }: { preview: GanttPreview; window: TimelineWindow; top: number }) => {
  if (preview.segments && preview.segments.length > 0) {
    return <PreviewSegments preview={preview} segments={preview.segments} window={window} top={top} />;
  }
  const g = barGeometry(preview.from, preview.to, window);
  const color = preview.late ? 'error.main' : 'success.main';
  if (!g) {
    const after = new Date(preview.from).getTime() > window.to.getTime();
    return (
      <Box
        data-testid="gantt-preview"
        sx={{
          position: 'absolute',
          top,
          [after ? 'right' : 'left']: 4,
          height: TASK_H,
          px: 1,
          lineHeight: `${TASK_H - 4}px`,
          fontSize: '0.7rem',
          fontWeight: 600,
          border: '2px dashed',
          borderColor: color,
          borderRadius: 13,
          bgcolor: 'background.paper',
          color,
          zIndex: 4,
          pointerEvents: 'none',
        }}
      >
        {after ? `${shortDateTime(preview.from)} →` : `← ${shortDateTime(preview.from)}`}
      </Box>
    );
  }
  return (
    <Box
      data-testid="gantt-preview"
      sx={[
        placed(g, top),
        {
          px: 0.5,
          lineHeight: `${TASK_H - 4}px`,
          fontSize: '0.7rem',
          fontWeight: 600,
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          border: '2px dashed',
          borderColor: color,
          borderRadius: 1,
          color,
          zIndex: 4,
          pointerEvents: 'none',
        },
        (theme: Theme) => ({
          backgroundColor: alpha(preview.late ? theme.palette.error.main : theme.palette.success.main, 0.12),
        }),
      ]}
    >
      {preview.label}
    </Box>
  );
};

/** Odcinek czynności kierowcy — z tachografu albo z planu (szacunek). */
const ActivityBox = ({
  kind,
  start,
  end,
  window,
  top,
  tip,
  estimate,
  testId,
  trackWidth,
}: {
  kind: DriverActivityKind;
  start: Date | string;
  end: Date | string;
  window: TimelineWindow;
  top: number;
  tip: string;
  estimate: boolean;
  testId?: string;
  trackWidth: number;
}) => {
  const g = barGeometry(start, end, window, 0);
  if (!g || g.width <= 0) return null;
  const c = ACTIVITY_COLORS[kind];
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const wide = (g.width * trackWidth) / 100 >= 40 && !g.clippedEnd;
  return (
    <Box
      data-testid={testId}
      data-kind={kind}
      data-estimate={estimate ? 'true' : undefined}
      data-tip={tip}
      // Z klawiatury (prototyp, zmiana 9): fokus pokazuje ten sam dymek co mysz.
      tabIndex={0}
      role="img"
      aria-label={tip.split('\n')[0]}
      sx={{
        ...placed(g, top),
        minWidth: kind === 'break' ? 3 : undefined,
        '&:focus-visible': { outline: '2px solid', outlineColor: 'text.primary', outlineOffset: '1px' },
        zIndex: 1,
        borderRadius: 0.75,
        ...clipRadius(g),
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        fontSize: '0.7rem',
        fontWeight: 500,
        lineHeight: `${TASK_H - 2}px`,
        textAlign: 'right',
        pr: wide ? 0.5 : 0,
        color: c.fg,
        backgroundColor: c.bg,
        border: '1px solid rgba(0,0,0,0.18)',
        ...(kind === 'unknown'
          ? { backgroundImage: 'repeating-linear-gradient(45deg, rgba(0,0,0,0.08) 0 3px, transparent 3px 6px)' }
          : {}),
        // Szacunek z planu — jaśniej i z przerywaną ramką, żeby nie udawał faktu.
        ...(estimate ? { opacity: 0.55, border: '1px dashed rgba(0,0,0,0.6)' } : {}),
      }}
    >
      {wide ? hoursText(ms / 3_600_000) : ''}
    </Box>
  );
};

/** Tydzień jazdy: ramka tygodnia, wypełnienie = udział w limicie; przekroczenie czerwone z procentem. */
const WeekBar = ({
  week,
  window,
  top,
  trackWidth,
  pastOnly = false,
}: {
  week: WeekDriving;
  window: TimelineWindow;
  top: number;
  trackWidth: number;
  /** Oś starsza niż parametr (S-24.10-07) — dopisek, do kiedy są dane. */
  pastOnly?: boolean;
}) => {
  const box = barGeometry(week.weekStart, new Date(week.weekEnd.getTime() - 1), window, 0);
  if (!box) return null;
  const span = week.weekEnd.getTime() - week.weekStart.getTime();
  const fillEnd = new Date(week.weekStart.getTime() + Math.min(week.share, 1) * span);
  const fill = week.totalH > 0 ? barGeometry(week.weekStart, fillEnd, window, 0) : null;
  const red = '#d32f2f';
  const lines = [
    `Tygodniowy czas jazdy, tydzień ${shortDateTime(week.weekStart.toISOString()).slice(0, 5)}–${shortDateTime(
      new Date(week.weekEnd.getTime() - 1).toISOString(),
    ).slice(0, 5)}:`,
    `${durationText(week.totalH * 3_600_000)} z ${week.limitH} h limitu (${week.percent}%) — 561/2006, art. 6 ust. 2`,
    `Wg tachografu: ${durationText(week.actualH * 3_600_000)}${
      week.coveredFrom && new Date(week.coveredFrom).getTime() > week.weekStart.getTime()
        ? ` (dane od ${shortDateTime(week.coveredFrom)})`
        : ''
    }`,
  ];
  if (pastOnly && new Date(week.coveredUntil).getTime() < week.weekEnd.getTime()) {
    lines.push(`Dane z tachografu do ${shortDateTime(week.coveredUntil)} (starsze niż parametr świeżości)`);
  }
  if (week.estimateH > 0) lines.push(`Z planu obsadzeń (szacunek): ${durationText(week.estimateH * 3_600_000)}`);
  if (week.over) lines.push(`⚠ Limit przekroczony o ${hoursText(week.totalH - week.limitH)}`);
  const tip = lines.join('\n');
  const text = `${hoursText(week.totalH).replace(' h', '')} / ${week.limitH} h${week.over ? ` · ${week.percent}%` : ''}`;
  const boxPx = (box.width * trackWidth) / 100;
  return (
    <>
      <Box
        data-testid={`gantt-week-${week.weekStart.toISOString()}`}
        data-over={week.over ? 'true' : undefined}
        data-tip={tip}
        // Z klawiatury (prototyp, zmiana 9) — pasek tygodnia ma ten sam dymek.
        tabIndex={0}
        role="img"
        aria-label={lines[1]}
        sx={{
          ...placed(box, top),
          '&:focus-visible': { outline: '2px solid', outlineColor: 'text.primary', outlineOffset: '1px' },
          zIndex: 1,
          borderRadius: 0.75,
          ...clipRadius(box),
          border: '1px solid',
          borderColor: week.over ? red : alpha(SLATE, 0.55),
          backgroundColor: alpha(SLATE, 0.16),
        }}
      />
      {fill ? (
        <Box
          data-tip={tip}
          sx={{
            ...placed(fill, top),
            zIndex: 1,
            borderRadius: 0.75,
            backgroundColor: week.over ? red : SLATE,
            ...(week.estimateH > 0 && week.actualH === 0 ? { opacity: 0.6 } : {}),
          }}
        />
      ) : null}
      {/* Napis nad wypełnieniem i ramką, w jednym miejscu — czytelny przy każdym udziale. */}
      <Box
        data-tip={tip}
        sx={{
          position: 'absolute',
          left: `${box.left}%`,
          width: `${box.width}%`,
          top,
          height: TASK_H,
          zIndex: 2,
          px: 0.75,
          lineHeight: `${TASK_H}px`,
          fontSize: '0.7rem',
          fontWeight: 600,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textAlign: 'right',
          color: week.over ? red : 'text.primary',
        }}
      >
        {boxPx >= 70 ? text : week.over && boxPx >= 34 ? `${week.percent}%` : ''}
      </Box>
    </>
  );
};

const activityTip = (
  label: string,
  start: Date | string,
  end: Date | string,
  extra: string[] = [],
): string =>
  [
    `${label}: ${segmentRange(new Date(start).toISOString(), new Date(end).toISOString())} · ${durationText(
      new Date(end).getTime() - new Date(start).getTime(),
    )}`,
    ...extra,
  ].join('\n');

/** Wiersz aktywności kierowcy. */
const DriverTrack = ({
  props,
  trackWidth,
  background,
}: {
  props: Props;
  trackWidth: number;
  background: ReactNode;
}) => {
  const { window, driverWeeks, activity, activityError, items } = props;
  const now = props.now ?? new Date();
  // Świeża oś albo — od 24.10.2026 (S-24.10-07) — przeszłość osi starszej niż parametr.
  const ok = activityDrawable(activity);
  const pastOnly = activity?.pastOnly === true;
  const estimateFrom = estimateStart(activity, now);
  const estimates = estimateFrom ? estimatedActivity(items, estimateFrom) : [];
  const weeks = weeklyDriving(activity, items, now);
  let message: string | null = null;
  if (activityError) message = `Nie udało się wczytać aktywności kierowcy: ${activityError}`;
  else if (!activity) message = 'Wczytuję aktywność kierowcy…';
  else if (!ok) message = `Brak danych z tachografu. ${activity.reason ?? ''}`.trim();

  return (
    <Box
      data-testid="gantt-track-driver"
      sx={{ position: 'relative', height: DRIVER_ROW, borderBottom: '1px solid', borderColor: 'divider', overflow: 'hidden' }}
    >
      {background}
      {driverWeeks.map((week) => {
        const b = driverWeekBounds(week);
        const g = barGeometry(b.from, new Date(b.to.getTime() - 1), window);
        if (!g) return null;
        const replicated = isReplicatedWeek(week);
        return (
          <Box
            key={week.weekStart}
            data-testid={`driver-week-${week.weekStart}`}
            data-tip={driverWeekTitle(week)}
            sx={{
              ...placed(g, 2, 16),
              zIndex: 1,
              px: 0.5,
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              fontSize: '0.68rem',
              lineHeight: '14px',
              border: '1px solid',
              borderRadius: 0.5,
              bgcolor: 'background.paper',
              ...(replicated
                ? { borderColor: 'divider', borderStyle: 'dotted', color: 'text.secondary', fontStyle: 'italic' }
                : { borderColor: 'info.main', color: 'text.primary' }),
            }}
          >
            {driverWeekText(week)}
          </Box>
        );
      })}
      {message ? (
        <Typography
          variant="caption"
          data-testid="driver-activity-message"
          sx={{ position: 'absolute', left: 8, right: 8, top: 30, color: 'text.secondary', lineHeight: 1.3 }}
        >
          {message}
        </Typography>
      ) : null}
      {ok && activity
        ? activity.spans.map((s, i) => (
            <ActivityBox
              key={`${s.start}-${i}`}
              kind={s.kind}
              start={s.start}
              end={s.end}
              window={window}
              top={26}
              estimate={false}
              testId={`driver-activity-${i}`}
              trackWidth={trackWidth}
              tip={activityTip(`${ACTIVITY_LABELS[s.kind]} (tachograf)`, s.start, s.end, [
                ...(s.open ? ['Czynność trwała w chwili pobrania danych.'] : []),
                ...(s.otherVehicle && s.vehiclePlate ? [`Innym pojazdem: ${s.vehiclePlate}`] : []),
                ...(s.kind === 'unknown' || s.kind === 'availability'
                  ? ['Nie liczymy jako jazdy ani odpoczynku.']
                  : []),
              ])}
            />
          ))
        : null}
      {estimates.map((a, i) => (
        <ActivityBox
          key={`est-${a.orderId}-${a.start.getTime()}-${i}`}
          kind={a.kind}
          start={a.start}
          end={a.end}
          window={window}
          top={26}
          estimate
          testId={`driver-estimate-${i}`}
          trackWidth={trackWidth}
          tip={activityTip(`${ACTIVITY_LABELS[a.kind]} (szacunek z planu)`, a.start, a.end, [
            `Zlecenie ${a.number ?? `#${a.orderId}`} — odcinek szacunku wykonania, nie dane z tachografu.`,
          ])}
        />
      ))}
      {weeks.map((w) => (
        <WeekBar
          key={w.weekStart.toISOString()}
          week={w}
          window={window}
          top={62}
          trackWidth={trackWidth}
          pastOnly={pastOnly}
        />
      ))}
    </Box>
  );
};

/** Etykieta wiersza — kolorowy pasek z lewej, jak w prototypie. */
const RowLabel = ({
  children,
  color,
  height,
  muted,
  testId,
}: {
  children: ReactNode;
  color: (theme: Theme) => string;
  height: number;
  muted?: boolean;
  testId?: string;
}) => (
  <Box
    data-testid={testId}
    sx={[
      {
        height,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        px: 1,
        fontSize: '0.8125rem',
        lineHeight: 1.25,
        fontWeight: muted ? 500 : 600,
        color: muted ? 'text.secondary' : 'text.primary',
        bgcolor: 'action.hover',
        borderBottom: '1px solid',
        borderRight: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
        boxSizing: 'border-box',
      },
      (theme: Theme) => ({ borderLeft: `4px solid ${color(theme)}` }),
    ]}
  >
    {children}
  </Box>
);

export const VehicleGantt = (outer: Props) => {
  // Nawiasy Z/R (24.10.2026) — z przełącznika, chyba że rodzic przekazał wprost.
  const bracketsOn = useFeature(GANTT_WINDOW_BRACKETS);
  const runningOn = useFeature(RUNNING_ORDER_NOT_VARIANT);
  const props: Props = {
    ...outer,
    windowBrackets: outer.windowBrackets ?? bracketsOn,
    runningNotVariant: outer.runningNotVariant ?? runningOn,
  };
  const { window, days, items, months, detail, onHighlight } = props;
  const now = props.now ?? new Date();
  const [trackRef, trackWidth] = useElementWidth<HTMLDivElement>();
  const dayList = timelineDays(window);
  const dayBoxes = dayList.map((d) => {
    const g = barGeometry(d.start, d.end, window);
    return { ...d, left: g?.left ?? 0, width: g?.width ?? 0 };
  });
  const parts = hourParts(days);
  const hours = hourLabels(parts);
  const nowPct = nowOffset(now, window);
  const todayKey = dayList.find((d) => now >= d.start && now < d.end)?.key;
  const dense = days > 21;
  // Szerokość jednego dnia (px) — podpis dnia w jednej linii albo w dwóch.
  const headerLayout = dayHeaderLayout(trackWidth > 0 ? trackWidth / Math.max(dayList.length, 1) : 0, parts);

  const background = (
    <>
      {dayBoxes.map((d) => (
        <Box
          key={d.key}
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${d.left}%`,
            width: `${d.width}%`,
            borderLeft: '1px solid',
            borderColor: 'divider',
            bgcolor: d.weekend ? 'action.hover' : undefined,
            // Podziałka godzinowa (zmiana 7) — cienkie kreski w dniu.
            backgroundImage:
              parts > 1
                ? `linear-gradient(to right, rgba(0,0,0,0.06) 1px, transparent 1px)`
                : undefined,
            backgroundSize: parts > 1 ? `${100 / parts}% 100%` : undefined,
          }}
        />
      ))}
      {months
        .filter((m) => m.unavailable)
        .map((m) => {
          const g = barGeometry(m.from, m.to, window);
          return g ? (
            <Box
              key={m.id}
              data-testid={`plan-month-${m.id}`}
              data-tip={unavailableMonthTitle(m.status)}
              sx={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: `${g.left}%`,
                width: `${g.width}%`,
                backgroundImage: 'repeating-linear-gradient(45deg, rgba(237,108,2,0.18) 0 6px, transparent 6px 12px)',
              }}
            />
          ) : null;
        })}
      {nowPct !== null ? (
        <Box
          data-testid="plan-now"
          data-tip={`Teraz: ${shortDateTime(now.toISOString())}`}
          sx={{ position: 'absolute', top: 0, bottom: 0, left: `${nowPct}%`, width: '2px', bgcolor: 'error.main', zIndex: 3 }}
        />
      ) : null}
    </>
  );

  const variantsLane = props.variantsLane === true;
  const compact = props.compact === true;
  // Oś tachografu starsza niż parametr (S-24.10-07): podpis i przejście do dnia odczytu.
  const staleNote = staleCaption(props.activity);
  const staleOutOfView =
    staleNote !== null && !!props.activity?.until && new Date(props.activity.until).getTime() < window.from.getTime();
  const lanes = ganttLanes(variantsLane).filter((l) => !compact || COMPACT_LANES.includes(l.lane));
  const byLane = (lane: GanttLane) =>
    items
      .filter((i) => laneOf(i, variantsLane, props.runningNotVariant === true) === lane)
      .sort(
        (a, b) =>
          new Date(a.occupancyFrom ?? a.windowFrom ?? 0).getTime() -
          new Date(b.occupancyFrom ?? b.windowFrom ?? 0).getTime(),
      );

  const orderOf = (target: EventTarget | null): number | null => {
    if (!(target instanceof Element)) return null;
    const el = target.closest<HTMLElement>('[data-order]');
    return el ? Number(el.dataset.order) : null;
  };

  return (
    <TipLayer>
      <Box
        data-testid={compact ? 'vehicle-gantt-compact' : 'vehicle-gantt'}
        data-detail={detail}
        data-days={days}
        onMouseOver={(e) => onHighlight(orderOf(e.target))}
        onMouseLeave={() => onHighlight(null)}
        onFocus={(e) => onHighlight(orderOf(e.target))}
        onBlur={() => onHighlight(null)}
        sx={{ display: 'grid', gridTemplateColumns: `${LABEL_WIDTH}px minmax(0, 1fr)`, width: '100%' }}
      >
        <Box sx={{ height: 44, borderBottom: '1px solid', borderColor: 'divider' }} />
        <Box ref={trackRef} sx={{ position: 'relative', height: 44, borderBottom: '1px solid', borderColor: 'divider' }}>
          {dayBoxes.map((d) => (
            <Box
              key={d.key}
              data-testid={`gantt-day-${d.key}`}
              sx={{
                position: 'absolute',
                left: `${d.left}%`,
                width: `${d.width}%`,
                top: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end',
                overflow: 'hidden',
                borderLeft: '1px solid',
                borderColor: 'divider',
                textAlign: 'center',
                whiteSpace: 'nowrap',
                fontSize: dense ? '0.68rem' : '0.75rem',
                lineHeight: '15px',
                letterSpacing: dense ? 0 : 0.4,
                color: d.weekend ? 'text.secondary' : 'text.primary',
              }}
            >
              <Box
                component="span"
                sx={d.key === todayKey ? { fontWeight: 700, color: 'error.main' } : undefined}
              >
                {/* Podpis dnia dopasowany do szerokości (29.10.2026) — przy 14 dniach
                    w 1024 px „cz 22.10” w jednej linii nachodziło na sąsiada. */}
                {headerLayout === 'oneLine' ? (
                  `${d.weekday} ${d.date}`
                ) : (
                  <>
                    {d.weekday}
                    <br />
                    {headerLayout === 'dayOfMonth' ? d.date.slice(0, 2) : d.date}
                  </>
                )}
              </Box>
              {hours.length > 0 ? (
                <Box
                  component="span"
                  data-testid="gantt-hours"
                  sx={{ display: 'flex', fontSize: '0.625rem', lineHeight: '14px', color: 'text.secondary', textAlign: 'left' }}
                >
                  {hours.map((h, i) => (
                    <Box
                      key={h}
                      component="span"
                      sx={{
                        flex: '1 0 0',
                        minWidth: 0,
                        overflow: 'hidden',
                        pl: '3px',
                        borderLeft: i === 0 ? 0 : '1px solid rgba(0,0,0,0.05)',
                      }}
                    >
                      {h}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Box>
          ))}
        </Box>

        {lanes.map(({ lane, label }) => {
          const laneItems = byLane(lane);
          return (
            <LaneRow
              key={lane}
              lane={lane}
              label={label}
              items={laneItems}
              props={props}
              trackWidth={trackWidth}
              background={background}
            />
          );
        })}

        {compact ? null : (
          <>
            <RowLabel color={() => SLATE} height={DRIVER_ROW} testId="gantt-label-driver">
              Aktywność kierowcy
              {staleNote ? (
                <>
                  {/* S-24.10-07: oś starsza niż parametr — przeszłość zostaje z podpisem. */}
                  <Typography
                    component="span"
                    variant="caption"
                    data-testid="driver-activity-stale"
                    data-tip={props.activity?.reason ?? undefined}
                    sx={{ color: 'warning.dark', fontWeight: 500, lineHeight: 1.2 }}
                  >
                    {staleNote}
                  </Typography>
                  {staleOutOfView && props.onShowDate ? (
                    <Box
                      component="button"
                      type="button"
                      data-testid="driver-activity-show-last"
                      onClick={() => props.onShowDate?.(new Date(props.activity?.until as string))}
                      sx={{
                        border: 0,
                        p: 0,
                        bgcolor: 'transparent',
                        color: 'primary.main',
                        cursor: 'pointer',
                        font: 'inherit',
                        fontSize: '0.7rem',
                        textDecoration: 'underline',
                      }}
                    >
                      Pokaż ostatni odczyt
                    </Box>
                  ) : null}
                </>
              ) : (
                <Typography component="span" variant="caption" sx={{ color: 'text.secondary', fontWeight: 400, lineHeight: 1.2 }}>
                  tachograf, dalej plan (szacunek)
                </Typography>
              )}
            </RowLabel>
            <DriverTrack props={props} trackWidth={trackWidth} background={background} />
          </>
        )}
      </Box>
    </TipLayer>
  );
};

/** Wiersz zleceń: etykieta i tor mają tę samą wysokość — układ liczony raz. */
const LaneRow = ({
  lane,
  label,
  items,
  props,
  trackWidth,
  background,
}: {
  lane: GanttLane;
  label: string;
  items: PlanItem[];
  props: Props;
  trackWidth: number;
  background: ReactNode;
}) => {
  const layout = laneLayout(lane, items, props);
  return (
    <>
      <RowLabel
        // Wiersz wariantów (makieta FTL) — zieleń ramki wariantu, nie pomarańcz
        // (pomarańcz tylko dla ostrzeżeń, prototyp, zmiana 4).
        color={(theme) => (lane === 'variants' ? theme.palette.success.dark : phaseTones(theme, lane).main)}
        height={layout.height}
        muted={layout.empty}
        testId={`gantt-label-${lane}`}
      >
        {label}
      </RowLabel>
      <LaneTrack lane={lane} layout={layout} props={props} trackWidth={trackWidth} background={background} />
    </>
  );
};
